import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { FOCUSABLE_ELEMENTS, KEY_CODES, KEY_DOWN } from '@outdoor-voices/ui/dist/config/constants';
import { lockScroll, unlockScroll } from '@outdoor-voices/ui/dist/utils/dom/scroll';
import { ChevronDown, ChevronUp, XLarge } from '@outdoor-voices/ui';
import cc from 'classcat';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useRef, useState } from 'react';
import css from './LightBox.module.scss';
import Image from '../../../components/Image';
import Play from '../../../components/icons/Play';
import ScrollSnapper from '../../../components/ScrollSnapper';
import stripHtmlTags from '../../../utils/strings/stripHtmlTags';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var IMAGE_SIZES = [600, 860];
var isVertical = true;

var LightBox = function LightBox(_ref) {
  var childClass = _ref.childClass,
      description = _ref.description,
      goTo = _ref.goTo,
      hide = _ref.hide,
      images = _ref.images,
      index = _ref.index,
      initialIndex = _ref.initialIndex,
      parentClass = _ref.parentClass,
      scaledToFit = _ref.scaledToFit,
      scrollerRef = _ref.scrollerRef,
      title = _ref.title,
      subtitle = _ref.subtitle;

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      showVideoPoster = _useState2[0],
      setShowVideoPoster = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      locked = _useState4[0],
      setLocked = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      showing = _useState6[0],
      setShowing = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      hiding = _useState8[0],
      setHiding = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      on = _useState10[0],
      setOn = _useState10[1];

  var rootRef = useRef(null);
  var copyRef = useRef(null);
  var videoRef = useRef(null);
  var closeButton = useRef(null);
  useEffect(function () {
    var onKeyDown = function onKeyDown(event) {
      var ARROW_DOWN = KEY_CODES.ARROW_DOWN,
          ARROW_LEFT = KEY_CODES.ARROW_LEFT,
          ARROW_RIGHT = KEY_CODES.ARROW_RIGHT,
          ARROW_UP = KEY_CODES.ARROW_UP,
          ESC = KEY_CODES.ESC,
          TAB = KEY_CODES.TAB;

      switch (event.key) {
        case ESC:
          setHiding(true);
          break;

        case ARROW_LEFT:
        case ARROW_UP:
          goTo(index - 1);
          break;

        case ARROW_DOWN:
        case ARROW_RIGHT:
          goTo(index + 1);
          break;

        default:
          break;
      } // add all the elements which you want to make focusable


      var focusableContent = rootRef.current.querySelectorAll(FOCUSABLE_ELEMENTS);
      var first = focusableContent[0];
      var last = focusableContent[focusableContent.length - 1];
      var isTabPressed = event.key === TAB;

      if (!isTabPressed) {
        return;
      }

      var activeElementIndex = _toConsumableArray(focusableContent).indexOf(document.activeElement);

      if (event.shiftKey) {
        // if shift key pressed for shift + tab combination
        if (document.activeElement === first || activeElementIndex < 0) {
          last.focus(); // add focus for the last focusable element

          event.preventDefault();
        }
      } else {
        // if tab key is pressed
        // eslint-disable-next-line no-lonely-if
        if (document.activeElement === last || activeElementIndex < 0) {
          // if focused has reached to last focusable element then focus first focusable element after pressing tab
          first.focus();
          event.preventDefault();
        }
      }
    };

    document.addEventListener(KEY_DOWN, onKeyDown, false);

    if (!on) {
      setOn(true);
    }

    return function () {
      document.removeEventListener(KEY_DOWN, onKeyDown);
    };
  }, [on, videoRef, goTo, initialIndex, index]);
  useEffect(function () {
    if (!on) {
      return;
    }

    var onShow = function onShow() {
      lockScroll();
      goTo(initialIndex, function () {}, true);
      setLocked(true);
      setShowing(true);
    };

    var timeout = null;
    timeout = setTimeout(onShow, 150);
    return function () {
      clearInterval(timeout);
    };
  }, [on, goTo, initialIndex]);
  useEffect(function () {
    if (!hiding || !locked) {
      return;
    }

    var pauseVideo = function pauseVideo() {
      if (!videoRef.current) {
        return;
      }

      videoRef.current.pause();
      setShowVideoPoster(true);
    };

    pauseVideo();
    unlockScroll();
    setLocked(false);
    setShowing(false);
    setTimeout(hide, 300);
  }, [hiding, hide, locked, videoRef]);

  var getDescription = function getDescription() {
    if (!description) {
      return description;
    }

    return stripHtmlTags(description);
  };

  var getAsset = function getAsset(_ref2) {
    var id = _ref2.id,
        image = _ref2.image,
        video = _ref2.video;

    if (video) {
      var autoplay = video.autoplay,
          loop = video.loop,
          muted = video.muted,
          poster = video.poster,
          src = video.src,
          subtitleUrl = video.subtitleUrl;

      var kid = /*#__PURE__*/_jsx("div", {
        className: cc([css.image, childClass]),
        children: /*#__PURE__*/_jsxs("video", {
          autoPlay: autoplay,
          className: cc([css.video, _defineProperty({}, css.showVideo, !showVideoPoster || autoplay)]),
          controls: !showVideoPoster && !loop,
          controlsList: "nodownload",
          crossOrigin: "anonymous",
          loop: loop,
          muted: loop,
          preload: "metadata",
          poster: poster.src,
          ref: videoRef,
          tabIndex: -1,
          children: [/*#__PURE__*/_jsx("source", {
            src: src,
            type: "video/mp4"
          }), /*#__PURE__*/_jsx("track", {
            label: "english".concat(muted ? '_description' : ''),
            kind: "descriptions",
            srcLang: "en",
            src: subtitleUrl,
            default: true
          })]
        })
      }, "img-".concat(id));

      if (autoplay) {
        return kid;
      }

      return /*#__PURE__*/_jsxs("button", {
        "aria-label": "Play Video",
        className: cc([css.image, childClass]),
        onClick: function onClick() {
          if (showVideoPoster) {
            playVideo();
          }
        },
        tabIndex: showing ? 0 : -1,
        type: "button",
        children: [kid, /*#__PURE__*/_jsxs("div", {
          className: cc([_defineProperty({}, css.hiddenVideoPosterWrap, !showVideoPoster)]),
          children: [/*#__PURE__*/_jsx(Image, _objectSpread(_objectSpread({}, poster), {}, {
            alt: "Video Poster",
            className: css.videoPoster,
            sizes: IMAGE_SIZES
          })), /*#__PURE__*/_jsx(Play, {})]
        })]
      }, "img-".concat(id));
    }

    if (image) {
      return /*#__PURE__*/_jsx("div", {
        className: cc([css.image, childClass, _defineProperty({}, css.scaledToFit, scaledToFit)]),
        children: /*#__PURE__*/_jsx(Image, _objectSpread(_objectSpread({}, image), {}, {
          alt: "Detail image of ".concat(image.alt),
          sizes: IMAGE_SIZES
        }))
      }, "img-".concat(image.id));
    }

    return false;
  };

  var playVideo = function playVideo() {
    if (!videoRef.current) {
      return;
    }

    setShowVideoPoster(false);
    videoRef.current.play();
  };

  var total = images.length;
  var descriptionFixed = getDescription();
  var arrowUpClass = cc([css.arrow, _defineProperty({}, css.disabled, index === 0)]);
  var arrowDownClass = cc([css.arrow, _defineProperty({}, css.disabled, index === total - 1)]);
  return /*#__PURE__*/_jsxs("div", {
    className: cc([css.root, _defineProperty({}, css.show, showing)]),
    ref: rootRef,
    children: [/*#__PURE__*/_jsx("button", {
      "aria-label": "Return To Product Details And Close Overlay",
      className: css.close,
      onClick: function onClick() {
        setHiding(true);
      },
      ref: closeButton,
      type: "button",
      title: "Close dialog",
      children: /*#__PURE__*/_jsx(XLarge, {})
    }), /*#__PURE__*/_jsxs("div", {
      className: css.content,
      children: [/*#__PURE__*/_jsx("div", {
        className: cc([css.images, parentClass]),
        ref: scrollerRef,
        children: images.map(getAsset)
      }), /*#__PURE__*/_jsxs("div", {
        className: css.details,
        ref: copyRef,
        children: [title && /*#__PURE__*/_jsx("div", {
          className: css.title,
          children: title
        }), subtitle, descriptionFixed && /*#__PURE__*/_jsx("div", {
          className: css.description,
          children: descriptionFixed
        }), images.length > 1 && /*#__PURE__*/_jsxs("div", {
          className: css.arrows,
          children: [/*#__PURE__*/_jsx("button", {
            "aria-label": "Navigate To The Previous Image",
            className: arrowUpClass,
            disabled: index === 0,
            onClick: function onClick() {
              return goTo(index - 1);
            },
            title: "Go To Previous Image",
            type: "button",
            children: /*#__PURE__*/_jsx(ChevronUp, {})
          }), /*#__PURE__*/_jsx("button", {
            "aria-label": "Navigate To The Next Image",
            className: arrowDownClass,
            disabled: index === total - 1,
            onClick: function onClick() {
              return goTo(index + 1);
            },
            title: "Go To Next Image",
            type: "button",
            children: /*#__PURE__*/_jsx(ChevronDown, {})
          })]
        })]
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: css.bg
    })]
  });
};

LightBox.defaultProps = {
  allowBgScroll: true,
  description: false,
  index: 0,
  initialIndex: 0,
  title: false,
  scaledToFit: false,
  show: false,
  subtitle: false
};
LightBox.propTypes = {
  allowBgScroll: PropTypes.bool,
  childClass: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  goTo: PropTypes.func.isRequired,
  hide: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  index: PropTypes.number,
  initialIndex: PropTypes.number,
  parentClass: PropTypes.string.isRequired,
  resetIndex: PropTypes.func.isRequired,
  show: PropTypes.bool,
  scaledToFit: PropTypes.bool,
  scrollerRef: PropTypes.shape().isRequired,
  title: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  subtitle: PropTypes.oneOfType([PropTypes.bool, PropTypes.element, PropTypes.node])
};
export default ScrollSnapper( /*#__PURE__*/memo(LightBox), isVertical);